import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useInventory } from '../context/InventoryContext';
import api from "../services/api";
import {
  FaBuilding, FaUserShield, FaUserCog,
  FaMicrochip, FaSimCard, FaCar,
  FaSitemap, FaSignOutAlt, FaChevronDown, FaChevronUp,
  FaTachometerAlt,
  FaClipboardList,
  FaChartBar,
  FaTrophy,
  FaCheckCircle,
  FaClock,
  FaBars,
  FaTimes,
  FaUsers,
  FaBell,
  FaCheck,
  FaTicketAlt,
  FaTruck,
  FaTag,
  FaCalendarAlt,
  FaSync,
  FaEye,
  FaUserCheck,
  FaEdit,
  FaUserTie,
  FaExclamationTriangle
} from 'react-icons/fa';
import GlobalNotificationListener from '../components/GlobalNotificationListener';
import '../App.css';
import { toast } from 'react-toastify';

const Layout = () => {
  const { currentUser, logout, users = [] } = useAuth();
  const { vehicles = [] } = useInventory();
  const navigate = useNavigate();
  const location = useLocation();

  const isTeamLead = currentUser?.role === 'teamlead';
  const isInstaller = currentUser?.role === 'installer';
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin';
  const isQueryUser = currentUser?.username === 'query';

  const [companyOpen, setCompanyOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [showLogPage, setShowLogPage] = useState(false);
  const [showQueriesModal, setShowQueriesModal] = useState(false);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [queryFilter, setQueryFilter] = useState('all');
  const [queriesList, setQueriesList] = useState([]);
  const [allQueriesData, setAllQueriesData] = useState([]);

  // Vehicle popup states for installer
  const [showVehiclePopup, setShowVehiclePopup] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [popupCurrentQueryId, setPopupCurrentQueryId] = useState(null);
  const [popupCurrentVehicleList, setPopupCurrentVehicleList] = useState([]);
  const [popupStatusFilter, setPopupStatusFilter] = useState('');

  // ========== NOTIFICATION SYSTEM ==========
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  // ========== APPROVAL MODAL STATE ==========
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);

  // ========== NEW: VEHICLE FCR MODAL STATE ==========
  const [showVehicleFcrModal, setShowVehicleFcrModal] = useState(false);
  const [selectedFcrVehicleData, setSelectedFcrVehicleData] = useState(null);
  const [allVehicleFcrData, setAllVehicleFcrData] = useState([]);

  // Change this if it is filtering incorrectly or looking at names broadly
  const teamLeadInstallerNames = useMemo(() => {
    if (!currentUser || currentUser.role !== 'teamlead') return [];
    const rosterData = users
      .filter(u => u.role === 'installer' && u.teamLeadId === currentUser.id);
      
    const userNames = rosterData.map(u => String(u.username || '').toLowerCase().trim());
    const userIds = rosterData.map(u => String(u.id || '').toLowerCase().trim());
    
    return [...new Set([...userNames, ...userIds])];
  }, [users, currentUser]);

 // ========== FETCH ALL QUERIES FOR LEADERBOARD ==========
const fetchAllQueries = async () => {
  if (!currentUser) return [];
    
  // Method 1: Try the dedicated leaderboard endpoint
  try {
    const response = await api.get("/queries/all-for-leaderboard", {
      params: {
        companyId: currentUser.companyId
      }
    });
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data;
    }
  } catch (error) {
    console.log('Method 1 failed:', error.message);
  }

  // Method 2: Try to get all queries without username filter
  try {
    const response = await api.get("/queries", {
      params: {
        companyId: currentUser.companyId,
      }
    });
    if (Array.isArray(response.data) && response.data.length > 0) {
      return response.data;
    }
  } catch (error) {
    console.log('Method 2 failed:', error.message);
  }

  // Method 3: Try with role as 'installer' and no username
  try {
    const response2 = await api.get("/queries", {
      params: {
        role: 'installer',
        companyId: currentUser.companyId,
      }
    });
    if (Array.isArray(response2.data) && response2.data.length > 0) {
      return response2.data;
    }
  } catch (error) {
    console.log('Method 3 failed:', error.message);
  }

  // Method 4: Try without any params
  try {
    const response4 = await api.get("/queries");
    if (Array.isArray(response4.data) && response4.data.length > 0) {
      return response4.data;
    }
  } catch (error) {
    console.log('Method 4 failed:', error.message);
  }

  return [];
};

  // ========== INITIALIZE REAL LIVE DATA FROM DATABASE ==========
  const initializeData = async () => {
    if (!currentUser) return;

    // ================= TEAM LEAD =================
    if (currentUser.role === "teamlead") {
      try {
        const response = await api.get("/queries", {
          params: {
            role: "teamlead",
            teamLeadId: currentUser.id
          }
        });
       
        const formattedQueries = response.data.map(q => ({
          ...q,
          ticketNo: q.ticket_no,
          companyName: q.company_name,
          subCompanyName: q.sub_company_name,
          installerName: q.installer_name,
          vehicleName: q.vehicle_name,
          vehicleCount: q.vehicle_count,
          queryType: q.query_type,
          serialNumber: q.serial_number,
          installationDate: q.installation_date,
          createdAt: q.created_at,
          updatedAt: q.updated_at,
          vehicleIds: q.vehicle_ids
        }));

        setQueriesList(formattedQueries);
        setAllQueriesData(formattedQueries);
        
        refreshNotifications();
      } catch (err) {
        console.error("Team Lead Query Error:", err);
        setQueriesList([]);
        setAllQueriesData([]);
      }
      return;
    }

   // ================= INSTALLER =================
if (currentUser.role === "installer") {
 
  const currentUsername = String(currentUser.username).toLowerCase().trim();
  
  // STEP 1: Fetch ALL queries for leaderboard
  const allQueries = await fetchAllQueries();
  if (allQueries.length > 0) {
    setAllQueriesData(allQueries); 
    // Log debug info
    const installers = users.filter(u => String(u.role).trim().toLowerCase() === "installer");
    installers.forEach(installer => {
      const count = allQueries.filter(q => {
        const qInstaller = String(q.installerName || q.installer_name || '').toLowerCase().trim();
        return qInstaller === String(installer.username).toLowerCase().trim();
      }).length;
    });
  } else {
    console.log('⚠️ No all queries fetched, using fallback');
  }

  // STEP 2: Fetch the installer's own queries for "My Queries"
  let myQueries = [];
  
  // Try multiple approaches to get the installer's queries
  try {
    const myResponse = await api.get("/queries", {
      params: {
        role: currentUser.role,
        companyId: currentUser.companyId,
        username: currentUser.username
      }
    });
    if (Array.isArray(myResponse.data) && myResponse.data.length > 0) {
      myQueries = myResponse.data;

    }
  } catch (error) {
    console.log('Username approach failed:', error.message);
  }
  
  // Fallback: filter from all queries
  if (myQueries.length === 0 && allQueries.length > 0) {
    myQueries = allQueries.filter(q => {
      const qInstaller = String(q.installerName || q.installer_name || '').toLowerCase().trim();
      return qInstaller === currentUsername;
    });
  }
  
  setQueriesList(myQueries);
  return;
}
    try {
      const response = await api.get("/queries", {
        params: {
          role: currentUser.role,
          companyId: currentUser.companyId,
          username: currentUser.username || currentUser.name || ""
        }
      });

      if (Array.isArray(response.data)) {
        setQueriesList(response.data);
        setAllQueriesData(response.data);
      }
    } catch (error) {
      console.error("Layout error initialization query data:", error);
      setQueriesList([]);
      setAllQueriesData([]);
    }
  };

  useEffect(() => {
  }, [queriesList]);

  // ========== AUTOMATIC LIVE SYNC FROM DATABASE LOOP ==========
  useEffect(() => {
    if (!currentUser) return;
    
    initializeData();

    const interval = setInterval(() => {
      initializeData();
      if (isSuperAdmin || isTeamLead) {
        refreshNotifications();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [currentUser, teamLeadInstallerNames, vehicles]);

  // ========== REFRESH NOTIFICATIONS WITH FCR DATA AND GPS TIME ==========
  const refreshNotifications = async () => {
    try {
        const params = {
            role: currentUser.role,
            companyId: currentUser.companyId,
            username: currentUser.username,
        };
        
        if (currentUser.role === 'teamlead') {
            params.teamLeadId = currentUser.id;
        }
        
        const response = await api.get("/queries", { params });

        if (currentUser.role === "super_admin" || currentUser.role === "teamlead") {
            const pendingApprovalOnly = response.data.filter(
                q => String(q.status).toLowerCase() === "pending approval"
            );
            
            const enrichedNotifications = await Promise.all(pendingApprovalOnly.map(async (q) => {
                try {
                    const vehicleNames = q.vehicle_name ? q.vehicle_name.split(',').map(v => v.trim()) : [];
                    const allVehicleFcrData = [];
                    let fcrData = null;
                    let fcrNumbers = {};
                    let gpsLastUpdate = null;
                    
                    for (const vehicleName of vehicleNames) {
                        let vehicleFcrData = null;
                        let fcrNumber = null;
                        
                        const vehicleFromList = vehicles.find(v => 
                            v.licensePlate && v.licensePlate.toLowerCase() === vehicleName.toLowerCase()
                        );
                        if (vehicleFromList) {
                            if (vehicleFromList.fcrData) {
                                vehicleFcrData = vehicleFromList.fcrData;
                                fcrNumber = vehicleFcrData.reportNumber || vehicleFcrData.report_number;
                            }
                            if (vehicleFromList.gps_time) {
                                gpsLastUpdate = vehicleFromList.gps_time;
                            }
                        }
                        
                        if (!vehicleFcrData) {
                            try {
                                const fcrResponse = await api.get(`/fcr-reports/vehicle-number/${encodeURIComponent(vehicleName)}`);
                                if (fcrResponse.data && fcrResponse.data.success && fcrResponse.data.data) {
                                    vehicleFcrData = fcrResponse.data.data;
                                    fcrNumber = vehicleFcrData.reportNumber || vehicleFcrData.report_number;
                                    if (vehicleFcrData.gps_time) {
                                        gpsLastUpdate = vehicleFcrData.gps_time;
                                    }
                                }
                            } catch (fcrErr) {
                                // Silent fail
                            }
                        }
                        
                        if (vehicleFcrData) {
                            if (fcrNumber) {
                                fcrNumbers[vehicleName] = fcrNumber;
                            }
                            allVehicleFcrData.push({
                                vehicleName: vehicleName,
                                fcrData: vehicleFcrData
                            });
                            if (!fcrData) {
                                fcrData = vehicleFcrData;
                            }
                        }
                    }
                    
                    if (fcrData) {
                        return { 
                            ...q, 
                            fcrData: fcrData,
                            allVehicleFcrData: allVehicleFcrData,
                            hasFcrData: true,
                            showLiveStatus: false,
                            fcrNumbers: fcrNumbers,
                            gpsLastUpdate: gpsLastUpdate
                        };
                    }
                    
                    return { ...q, hasFcrData: false, showLiveStatus: false, gpsLastUpdate: null };
                } catch (err) {
                    console.error(`Error fetching FCR for query ${q.id}:`, err);
                    return { ...q, hasFcrData: false, showLiveStatus: false, gpsLastUpdate: null };
                }
            }));
            
            setNotifications(enrichedNotifications);
            setNotificationCount(enrichedNotifications.length);
            
            return enrichedNotifications;
        }
        return [];
    } catch (err) {
        console.log(err);
        return [];
    }
  };

  // ========== DIRECT APPROVAL WITHOUT POPUP ==========
  const handleDirectApproval = async (notification) => {
    try {
      const serialNumber = notification.serial_number || 
                          notification.serialNumber || 
                          notification.serial || 
                          "";
      
      const fcrNumbers = notification.fcrNumbers || notification.fcr_numbers || {};
      
      await api.patch(`/queries/${notification.id}/status`, {
        status: "Completed",
        serialNumber: serialNumber
      });
      
      if (Object.keys(fcrNumbers).length > 0) {
        try {
          await api.post(`/queries/${notification.id}/save-fcr-numbers`, {
            vehicleFcrNumbers: fcrNumbers
          });
        } catch (saveErr) {
          console.error('Error saving FCR numbers on approval:', saveErr);
        }
      }
      
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      setNotificationCount(prev => Math.max(0, prev - 1));
      
      if (notifications.length <= 1) {
        setShowNotificationsModal(false);
      }
      
      toast.success('Installation approved successfully!');
      initializeData();
      refreshNotifications();
    } catch (err) {
      console.error("Approval error:", err);
      toast.error("Failed to approve installation.");
    }
  };

  // ========== UPDATED: HANDLE FETCH NOTIFICATION ==========
  const handleFetchNotification = async (notification) => {
    try {
        let allVehicleData = [];
        let fcrData = null;
        let fcrNumbers = {};
        
        const vehicleNames = notification.vehicle_name ? notification.vehicle_name.split(',').map(v => v.trim()) : [];
        
        for (const vehicleName of vehicleNames) {
            let vehicleFcrData = null;
            let fcrNumber = null;
            
            const vehicleFromList = vehicles.find(v => 
                v.licensePlate && v.licensePlate.toLowerCase() === vehicleName.toLowerCase()
            );
            if (vehicleFromList && vehicleFromList.fcrData) {
                vehicleFcrData = vehicleFromList.fcrData;
                fcrNumber = vehicleFcrData.reportNumber || vehicleFcrData.report_number;
            }
            
            if (!vehicleFcrData) {
                try {
                    const fcrResponse = await api.get(`/fcr-reports/vehicle-number/${encodeURIComponent(vehicleName)}`);
                    if (fcrResponse.data && fcrResponse.data.success && fcrResponse.data.data) {
                        vehicleFcrData = fcrResponse.data.data;
                        fcrNumber = vehicleFcrData.reportNumber || vehicleFcrData.report_number;
                    }
                } catch (err) {
                    if (err.response && err.response.status === 404) {
                        console.log(`ℹ️ No FCR found for vehicle: ${vehicleName}`);
                    }
                }
            }
            
            if (!vehicleFcrData) {
                const vehicleData = vehicles.find(v => 
                    v.licensePlate && v.licensePlate.toLowerCase() === vehicleName.toLowerCase()
                );
                if (vehicleData && vehicleData.id) {
                    try {
                        const fcrResponse = await api.get(`/fcr-reports/vehicle/${vehicleData.id}`);
                        if (fcrResponse.data && fcrResponse.data.success && fcrResponse.data.data) {
                            vehicleFcrData = fcrResponse.data.data;
                            fcrNumber = vehicleFcrData.reportNumber || vehicleFcrData.report_number;
                        }
                    } catch (fcrErr) {
                        console.error('Error fetching FCR by vehicle ID:', fcrErr);
                    }
                }
            }
            
            if (vehicleFcrData) {
                if (fcrNumber) {
                    fcrNumbers[vehicleName] = fcrNumber;
                }
                
                allVehicleData.push({
                    vehicleName: vehicleName,
                    fcrData: vehicleFcrData,
                    imei: vehicleFcrData.imei || 'N/A',
                    simNo: vehicleFcrData.simNo || 'N/A',
                    reportNumber: fcrNumber || 'N/A'
                });
                if (!fcrData) {
                    fcrData = vehicleFcrData;
                }
            }
        }
        
        if (fcrData) {
            const updatedNotifications = notifications.map(notif => {
                if (notif.id === notification.id) {
                    return { 
                        ...notif, 
                        fcrData: fcrData,
                        allVehicleFcrData: allVehicleData,
                        hasFcrData: true,
                        showLiveStatus: true,
                        fcrNumbers: fcrNumbers
                    };
                }
                return notif;
            });
            setNotifications(updatedNotifications);
            
            setQueriesList(prev => prev.map(q => {
                if (q.id === notification.id || q.query_id === notification.id) {
                    return { 
                        ...q, 
                        fcrData: fcrData,
                        allVehicleFcrData: allVehicleData,
                        hasFcrData: true,
                        showLiveStatus: true,
                        fcrNumbers: fcrNumbers,
                        fcr_numbers: fcrNumbers
                    };
                }
                return q;
            }));

            try {
                await api.post(`/queries/${notification.id}/save-fcr-numbers`, {
                    vehicleFcrNumbers: fcrNumbers
                });
                
            } catch (saveErr) {
                console.error('Error saving FCR numbers:', saveErr);
            }
            
            toast.success(`✅ Fetched data for ${allVehicleData.length} vehicle(s)`);
        } else {
            toast.info('No FCR data available for these vehicles');
        }
    } catch (err) {
        console.error('Error fetching FCR details:', err);
        toast.error('Failed to fetch vehicle data');
    }
  };

  // ========== NEW: OPEN VEHICLE FCR MODAL ==========
  const handleOpenVehicleFcrModal = (vehicleData, allVehicles) => {
    setSelectedFcrVehicleData(vehicleData);
    setAllVehicleFcrData(allVehicles || []);
    setShowVehicleFcrModal(true);
  };

  // ========== OPEN APPROVAL ==========
  const openApprovalModal = (notification) => {

    handleDirectApproval(notification);
  };

  // ========== HANDLE APPROVAL SUBMIT ==========
  const handleApprovalSubmit = async () => {
    try {
      await api.patch(`/queries/${pendingApproval.id}/status`, {
        status: "Completed",
        serialNumber: pendingApproval.serial_number || pendingApproval.serialNumber || ""
      });
      toast.success("Installation approved successfully and saved to database!");
      initializeData();
      setShowApprovalModal(false);
      setPendingApproval(null);
    } catch (err) {
      console.error("Approval save error:", err);
      toast.error("Failed to approve installation and save to database.");
    }
  };

  // ========== OPEN VEHICLE POPUP FOR INSTALLER ==========
  const openVehiclePopup = (query) => {
    
    let vehicleList = [];
    
    if (query.vehicleName) {
      vehicleList = query.vehicleName.split(",").map(v => v.trim()).filter(Boolean);
    } else if (query.vehicle_name) {
      vehicleList = query.vehicle_name.split(",").map(v => v.trim()).filter(Boolean);
    }
 
    
    setPopupCurrentVehicleList(vehicleList);
    setPopupCurrentQueryId(query.id || query.query_id);
    setSelectedVehicles(vehicleList);
    setPopupStatusFilter("");
    setShowVehiclePopup(true);
  };

  // ========== TOGGLE VEHICLE SELECTION IN POPUP ==========
  const toggleVehicleSelection = (vehicle) => {
    setSelectedVehicles(prev => {
      if (prev.includes(vehicle)) {
        return prev.filter(v => v !== vehicle);
      } else {
        return [...prev, vehicle];
      }
    });
  };

 // ========== HANDLE STATUS UPDATE FOR SELECTED VEHICLES (INSTALLER) ==========
const handleStatusUpdateForSelected = async () => {
  if (!popupStatusFilter) {
    toast.warning("Please select status");
    return;
  }

  if (selectedVehicles.length === 0) {
    toast.warning("Please select vehicles");
    return;
  }

  const currentQuery = queriesList.find(q => q.id === popupCurrentQueryId || q.query_id === popupCurrentQueryId);

  if (!currentQuery) {
    toast.error("Query not found");
    return;
  }

  const allVehicles = popupCurrentVehicleList;
  const remainingVehicles = allVehicles.filter(
    v => !selectedVehicles.includes(v)
  );

  const selectedVehicleIds = selectedVehicles
    .map(v => {
      const vehicle = vehicles.find(x => x.licensePlate && x.licensePlate.toLowerCase() === v.toLowerCase());
      return vehicle ? vehicle.id : null;
    })
    .filter(Boolean);

  try {
    if (popupStatusFilter === "Completed") {
      // ✅ REMOVED: FCR report check - now completes without FCR requirement
      
      const response = await api.put("/queries/split-vehicles", {
        currentQueryId: popupCurrentQueryId,
        remainingVehicles: remainingVehicles.join(", "),
        remainingCount: remainingVehicles.length,
        selectedVehicles: selectedVehicles.join(", "),
        selectedCount: selectedVehicles.length,
        targetInstaller: currentQuery.installerName || currentQuery.installer_name,
        targetStatus: "Pending Approval",
        selectedVehicleIds,
        queryDetails: currentQuery
      });

      if (response.status === 200 || response.data) {
        toast.success(`${selectedVehicles.length} vehicle(s) submitted for approval!`);
        initializeData();
        setShowVehiclePopup(false);
      } else {
        toast.error("Failed to submit for approval");
      }
      return;
    }

    const response = await api.put("/queries/split-vehicles", {
      currentQueryId: popupCurrentQueryId,
      remainingVehicles: remainingVehicles.join(", "),
      remainingCount: remainingVehicles.length,
      selectedVehicles: selectedVehicles.join(", "),
      selectedCount: selectedVehicles.length,
      targetInstaller: currentQuery.installerName || currentQuery.installer_name,
      targetStatus: popupStatusFilter,
      selectedVehicleIds,
      queryDetails: currentQuery
    });

    if (response.data) {
      toast.success("Status updated successfully");
      initializeData();
      setShowVehiclePopup(false);
    }
  } catch (err) {
    console.error("Status update error:", err);
    toast.error(err.response?.data?.error || "Status update failed.");
  }
};

 // ========== Handle installer updating query to complete ==========
const handleCompleteQuery = async (queryId) => {
  console.log(`🔍 Installer updating state on DB for query ${queryId}`);
  const query = queriesList.find(q => q.id === queryId || q.query_id === queryId);
  
  if (!query) {
    toast.error('Query not found');
    return;
  }
  
  if (query.status === 'Completed' || query.status === 'Pending Approval') {
    toast.warning('This query is already completed or pending approval');
    return;
  }
  
  try {
    // ✅ REMOVED: FCR report check - now completes without FCR requirement
    
    await api.patch(`/queries/${queryId}/status`, {
      status: 'Pending Approval'
    });

    setQueriesList(prev => prev.map(q => {
      if (q.id === queryId || q.query_id === queryId) {
        return {
          ...q,
          status: 'Pending Approval'
        };
      }
      return q;
    }));

    toast.success(`✅ Query submitted for admin approval!`);
    initializeData();
    refreshNotifications();
    
  } catch (error) {
    console.error('Error updating query status:', error);
    toast.error('Failed to update query status.');
  }
};

  // ========== Listen for serial modal requests from LogPage ==========
  useEffect(() => {
    const handleSerialModalRequested = () => {
      const queryId = localStorage.getItem('pendingSerialQueryId');
   
      
      if (queryId) {
        const serialNumber = prompt('Enter Serial Number:');
        
        if (serialNumber === null) {
          localStorage.removeItem('pendingSerialQueryId');
          return;
        }
        
        if (!serialNumber.trim()) {
          toast.warning('Serial number is required');
          localStorage.removeItem('pendingSerialQueryId');
          return;
        }
        
        const currentQueries = JSON.parse(localStorage.getItem('queriesList') || '[]');
        const query = currentQueries.find(q => q.id === parseInt(queryId) || q.id === queryId);
        
        if (!query) {
          toast.error('Query not found');
          localStorage.removeItem('pendingSerialQueryId');
          return;
        }
        
        const updatedQueries = currentQueries.map(q => {
          if (q.id === queryId || q.id === parseInt(queryId)) {
            return {
              ...q,
              status: 'Pending Approval',
              serialNumber: serialNumber.trim(),
              installationDate: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };
          }
          return q;
        });
        
        localStorage.setItem('queriesList', JSON.stringify(updatedQueries));
        setQueriesList(updatedQueries);
        
        toast.success(`Query #${queryId} submitted for approval!`);
        refreshNotifications();
        localStorage.removeItem('pendingSerialQueryId');
      }
    };

    window.addEventListener('serialModalRequested', handleSerialModalRequested);
    return () => {
      window.removeEventListener('serialModalRequested', handleSerialModalRequested);
    };
  }, [queriesList]);

  // ========== Initialize on mount ==========
  useEffect(() => {
    initializeData();

    const interval = setInterval(() => {
      if (isSuperAdmin || isTeamLead) {
        refreshNotifications();
      }
    }, 3000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  // ========== Helper Functions ==========
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isSidebarOpen) {
        closeSidebar();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSidebarOpen]);

  // ========== LEADERBOARD METRIC FOR ALL INSTALLERS ==========
const leaderboardData = useMemo(() => {
  if (!Array.isArray(users)) return [];

  let allInstallers = users.filter(u => String(u.role).trim().toLowerCase() === "installer");

  // ALWAYS use allQueriesData for leaderboard - this contains ALL queries
  const queriesForLeaderboard = allQueriesData.length > 0 ? allQueriesData : queriesList;

  return allInstallers.map(installer => {
    // Total installed vehicles
    const installedCount = Array.isArray(vehicles) ? vehicles.filter(v =>
      String(v.installer_id) === String(installer.id) ||
      String(v.installed_by).toLowerCase() === String(installer.username).toLowerCase()
    ).length : 0;

    // ✅ ONLY COMPLETED queries attended by this installer
    const queriesAttended = queriesForLeaderboard.filter(q => {
      const qInstaller = String(q.installerName || q.installer_name || '').toLowerCase().trim();
      const installerName = String(installer.username).toLowerCase().trim();
      const status = String(q.status || '').toLowerCase();
      
      // ✅ ONLY count queries with status 'completed', 'installed', or 'in use'
      const isCompleted = status === 'completed' || status === 'installed' || status === 'in use';
      
      return qInstaller === installerName && isCompleted;
    }).length;

    // Panic buttons installed
    let totalPanicButtons = 0;
    if (Array.isArray(vehicles)) {
      vehicles.forEach(v => {
        const isInstalledByThisInstaller = 
          String(v.installer_id) === String(installer.id) ||
          String(v.installed_by).toLowerCase() === String(installer.username).toLowerCase();
        if (isInstalledByThisInstaller) {
          const panicCount = parseInt(v.panic_buttons) || 0;
          totalPanicButtons += panicCount;
        }
      });
    }

    return {
      id: installer.id,
      username: installer.username,
      installedCount: installedCount,
      queriesAttended: queriesAttended,
      totalPanicButtons: totalPanicButtons
    };
  }).sort((a, b) => b.installedCount - a.installedCount);
}, [users, vehicles, allQueriesData, queriesList]);

  // ========== MY QUERIES - Only show the logged-in installer's queries ==========
  const installerQueries = useMemo(() => {
    if (!currentUser) return [];
    
    if (isInstaller) {
      const currentUsername = String(currentUser.username).toLowerCase().trim();
      return queriesList.filter(q => {
        const qInstaller = String(q.installerName || q.installer_name || '').toLowerCase().trim();
        return qInstaller === currentUsername;
      });
    }
    
    return queriesList;
  }, [queriesList, currentUser, isInstaller]);

  useEffect(() => {
    const checkLogPageAccess = () => {
      if (isSuperAdmin) {
        setShowLogPage(true);
        return;
      }

      if (isTeamLead) {
        setShowLogPage(true);
        return;
      }

      if (isAdmin) {
        const saved = localStorage.getItem('logPageEnabledCompanies');
        if (saved) {
          const enabledCompanies = JSON.parse(saved);
          const userCompanyId = currentUser?.companyId || currentUser?.id;
          const hasAccess = enabledCompanies.includes(String(userCompanyId));
          setShowLogPage(hasAccess);
    
        } else {
          setShowLogPage(false);
        }
        return;
      }

      setShowLogPage(false);
    };

    checkLogPageAccess();
    window.addEventListener('logPageAccessChanged', checkLogPageAccess);
    return () => {
      window.removeEventListener('logPageAccessChanged', checkLogPageAccess);
    };
  }, [isSuperAdmin, isAdmin, isTeamLead, currentUser]);

  useEffect(() => {
    if (['/company-details', '/sub-companies'].includes(location.pathname)) {
      setCompanyOpen(true);
    }
    if (['/admin-signup', '/installer-signup', '/installer-query-signup'].includes(location.pathname)) {
      setRegisterOpen(true);
    }
    if (['/reports', '/device-reports', '/installer-reports'].includes(location.pathname)) {
      setReportsOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      currentUser &&
      currentUser.role === 'installer' &&
      !isQueryUser &&
      location.pathname !== '/vehicle-details'
    ) {
      navigate('/vehicle-details', { replace: true });
    }
  }, [currentUser, isQueryUser, location.pathname, navigate]);

  const handleLogout = () => {
    if (currentUser?.role === 'installer') {
      navigate('/login', {
        state: {
          triggerLogoutCamera: true
        }
      });
      return;
    }

    logout();
    navigate('/login', { replace: true });
  };

  const filteredQueries = useMemo(() => {
    if (queryFilter === 'all') return installerQueries;

    return installerQueries.filter(v => {
      const currentStatus = String(v.status || '').toLowerCase();
      if (queryFilter === 'completed') {
        return currentStatus === 'completed' || currentStatus === 'installed' || currentStatus === 'in use';
      }
      if (queryFilter === 'pending') {
        return currentStatus === 'pending' || currentStatus === 'allotted' || currentStatus === '' || currentStatus === 'pending approval' || currentStatus === 'ongoing';
      }
      return true;
    });
  }, [installerQueries, queryFilter]);

  const formatDateToLocal = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}-${month}-${year} - ${hours}:${minutes}`;
    } catch (e) {
      return dateString;
    }
  };

  // Function to get FCR numbers from query
  const getFcrNumbers = (query) => {
    if (query.fcr_numbers) {
      try {
        const fcrData = typeof query.fcr_numbers === 'string' 
          ? JSON.parse(query.fcr_numbers) 
          : query.fcr_numbers;
        const fcrValues = Object.values(fcrData).filter(v => v && v !== '');
        if (fcrValues.length > 0) {
          return fcrValues;
        }
      } catch (e) {
        if (query.fcr_numbers) {
          return [query.fcr_numbers];
        }
      }
    }
    return [];
  };

  // ========== TEAMLEAD VIEW WITH SIDEBAR ==========
  if (isTeamLead) {
    return (
      <>
        <GlobalNotificationListener />
        <div className="min-h-screen flex bg-gray-50" style={{ minHeight: '100vh' }}>
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="fixed top-4 left-4 z-[9999] p-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg transition-all duration-200"
              style={{
                position: 'fixed',
                top: '16px',
                left: '16px',
                zIndex: 99999,
                minWidth: '48px',
                minHeight: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Toggle navigation menu"
            >
              {isSidebarOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
            </button>
          )}

          {isMobile && isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-[9998]"
              onClick={closeSidebar}
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
            />
          )}

          <aside
            className="fixed top-0 left-0 z-[9998] w-72 lg:w-64 h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white flex flex-col shadow-2xl overflow-y-auto"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              height: '100vh',
              overflowY: 'auto',
              zIndex: 9998,
              transform: isMobile ? (isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
              transition: 'transform 0.3s ease-in-out'
            }}
          >
            <div className="p-6 border-b border-gray-700 flex justify-center flex-shrink-0">
              <img src="/nXExtendLogo.png" alt="Company Logo" className="h-10 w-auto object-contain drop-shadow" />
            </div>

            <div className="px-3 py-3 border-b border-gray-700">
              <button
                onClick={() => {

                  setShowNotificationsModal(true);
                  refreshNotifications();
                }}
                className="relative w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 py-2.5 px-3 rounded-lg transition"
              >
                <FaBell className="text-lg" />
                <span className="text-sm font-medium">Notifications</span>
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {notificationCount}
                  </span>
                )}
              </button>
            </div>

            <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
              <SidebarLink to="/installer-signup" icon={<FaUsers />} label="Installers" onClick={closeSidebar} />
              <SidebarLink to="/vehicle-details" icon={<FaCar />} label="Vehicle Details" onClick={closeSidebar} />
              <SidebarLink to="/log-page" icon={<FaClipboardList />} label="Complaints" onClick={closeSidebar} />
              <SidebarLink to="/installer-reports" icon={<FaChartBar />} label="Installer Reports" onClick={closeSidebar} />
              <button
                onClick={() => {
                  setShowLeaderboardModal(true);
                  closeSidebar();
                }}
                className="flex items-center space-x-3 py-2.5 px-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200 w-full"
              >
                <FaTrophy className="text-lg" />
                <span>Leaderboard</span>
              </button>
            </nav>

            <div className="p-4 border-t border-gray-700 flex-shrink-0">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Logged in as</div>
              <div className="text-sm font-medium">{currentUser?.username} <span className="text-gray-400">(Team Lead)</span></div>
              <button onClick={() => { handleLogout(); closeSidebar(); }} className="mt-3 w-full flex items-center justify-center space-x-2 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm transition">
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          </aside>

          <main
            className="flex-1 min-h-screen bg-gray-50"
            style={{
              marginLeft: isMobile ? '0' : '16rem',
              width: isMobile ? '100%' : 'auto',
              minHeight: '100vh',
              flex: 1,
              position: 'relative'
            }}
          >
            {isMobile && <div className="h-16"></div>}
            <div className="p-3 sm:p-4 md:p-6 lg:p-8 w-full max-w-full overflow-x-hidden">
              <Outlet context={{ users, currentUser, vehicles }} />
            </div>
          </main>
        </div>

        {showNotificationsModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-auto max-h-[90vh] flex flex-col">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl flex justify-between items-center flex-shrink-0">
                <div className="flex items-center gap-3">
                  <FaBell className="text-white text-xl" />
                  <h2 className="text-white text-xl font-bold">
                    Pending Approvals ({notifications.length})
                  </h2>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      setShowNotificationsModal(false);
                      navigate('/log-page');
                    }}
                    className="text-white/80 hover:text-white text-sm bg-white/10 px-3 py-1.5 rounded-lg flex items-center gap-2 transition"
                  >
                    <FaClipboardList className="text-xs" /> View All
                  </button>
                  <button
                    onClick={() => setShowNotificationsModal(false)}
                    className="text-white/80 hover:text-white text-2xl transition"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <FaBell className="text-6xl mb-4 opacity-30" />
                    <p className="text-lg font-medium">No pending approvals</p>
                    <p className="text-sm">All caught up!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {notifications.map((n) => {
                      const fcrReport = n.fcrData || null;
                      const reportNumber = fcrReport?.reportNumber || n.serial_number || 'N/A';
                      const allVehicles = n.allVehicleFcrData || [];
                      const hasFcrData = n.hasFcrData || false;
                      const showLiveStatus = n.showLiveStatus || false;
                      const gpsLastUpdate = n.gpsLastUpdate || null;
                      
                      const formatGpsTime = (gpsTime) => {
                        if (!gpsTime) return 'No GPS data';
                        try {
                          const date = new Date(gpsTime);
                          if (isNaN(date.getTime())) return 'No GPS data';
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const day = String(date.getDate()).padStart(2, '0');
                          const hours = String(date.getHours()).padStart(2, '0');
                          const minutes = String(date.getMinutes()).padStart(2, '0');
                          return `${day}-${month}-${year} ${hours}:${minutes}`;
                        } catch (e) {
                          return 'No GPS data';
                        }
                      };
                      
                      return (
                        <div
                          key={n.id}
                          className="border rounded-xl p-5 transition shadow-sm bg-blue-50 border-blue-200 hover:shadow-md"
                        >
                          <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold px-3 py-1.5 rounded-full bg-blue-500 text-white flex items-center gap-1.5">
                                <FaClock className="text-[11px]" /> ⏳ PENDING APPROVAL
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDateToLocal(n.created_at || n.createdAt)}
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <button
                                onClick={() => handleDirectApproval(n)}
                                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition shadow-md"
                              >
                                <FaCheck className="text-sm" />
                                Approve
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
                            <div className="flex items-center gap-1">
                              <FaTicketAlt className="text-blue-500 text-[10px]" />
                              <span className="text-gray-600">Ticket:</span>
                              <span className="text-gray-900 font-mono font-bold">{n.ticket_no || `#${n.id}`}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FaBuilding className="text-purple-500 text-[10px]" />
                              <span className="text-gray-600">Company:</span>
                              <span className="text-gray-900 truncate">{n.company_name || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FaUserCog className="text-indigo-500 text-[10px]" />
                              <span className="text-gray-600">Installer:</span>
                              <span className="text-gray-900">{n.installer_name || 'Unknown'}</span>
                            </div>
                            <div className="flex items-center gap-1 col-span-2 md:col-span-1">
                              <FaTruck className="text-emerald-500 text-[10px]" />
                              <span className="text-gray-600">Vehicles:</span>
                              <span className="text-gray-900 text-xs truncate" title={n.vehicle_name}>
                                {n.vehicle_name || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FaMicrochip className="text-emerald-600 text-[10px]" />
                              <span className="text-gray-600">FCR:</span>
                              {allVehicles.length > 0 ? (
                                <span className="text-emerald-700 font-mono font-bold text-[10px]">
                                  {allVehicles.length} found
                                </span>
                              ) : (
                                <span className="text-gray-400 text-[10px] italic">No FCR</span>
                              )}
                            </div>
                           
                          </div>

                          {allVehicles.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs font-semibold text-gray-600 mb-1.5">Vehicle FCR Details:</div>
                              <div className="flex flex-wrap gap-1.5">
                                {allVehicles.map((item, idx) => (
                                  <button
                                    key={idx}
                                    onClick={() => handleOpenVehicleFcrModal(item, allVehicles)}
                                    className="text-xs bg-white px-3 py-1 rounded border border-gray-200 flex items-center gap-1.5 hover:bg-gray-50 hover:border-indigo-300 transition"
                                  >
                                    <span className="font-medium text-gray-700">{item.vehicleName}</span>
                                    {item.fcrData?.reportNumber && (
                                      <span className="text-emerald-600 font-mono text-[10px]">
                                        FCR: {item.fcrData.reportNumber}
                                      </span>
                                    )}
                                    <FaEye className="text-indigo-400 text-[10px] ml-1" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                            Installer <span className="font-medium text-gray-700">{n.installer_name || 'Unknown'}</span> submitted vehicle records awaiting approval.
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center flex-shrink-0 bg-gray-50 rounded-b-2xl flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-gray-500">
                    Total: <span className="font-bold">{notifications.length}</span> pending
                  </span>
                  <button
                    onClick={async () => {
                      try {
                        await refreshNotifications();
                        toast.success(`✅ Fetched ${notifications.length} pending approvals`);
                      } catch (err) {
                        console.error('Error fetching all notifications:', err);
                        toast.error('Failed to fetch notifications');
                      }
                    }}
                    className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-xs font-medium transition flex items-center gap-1"
                  >
                    <FaSync className="text-xs" /> Refresh All
                  </button>
                </div>
                <button
                  onClick={() => setShowNotificationsModal(false)}
                  className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

       {showVehicleFcrModal && selectedFcrVehicleData && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[90vh] flex flex-col">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 rounded-t-2xl flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-3">
          <FaTruck className="text-white text-xl" />
          <h2 className="text-white text-xl font-bold">
            Vehicle Live Data - {selectedFcrVehicleData.vehicleName}
          </h2>
        </div>
        <button
          onClick={() => {
            setShowVehicleFcrModal(false);
            setSelectedFcrVehicleData(null);
            setAllVehicleFcrData([]);
          }}
          className="text-white/80 hover:text-white text-2xl transition"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {selectedFcrVehicleData.fcrData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">Vehicle Number</div>
                <div className="text-base font-bold text-gray-900">{selectedFcrVehicleData.vehicleName || 'N/A'}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">FCR Report Number</div>
                <div className="text-base font-bold text-emerald-600">
                  {selectedFcrVehicleData.reportNumber || selectedFcrVehicleData.fcrData?.reportNumber || 'N/A'}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">IMEI</div>
                <div className="text-sm font-mono text-gray-700">{selectedFcrVehicleData.imei || selectedFcrVehicleData.fcrData?.imei || 'N/A'}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">SIM Number</div>
                <div className="text-sm font-mono text-gray-700">{selectedFcrVehicleData.simNo || selectedFcrVehicleData.fcrData?.simNo || 'N/A'}</div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FaEye className="text-indigo-500" /> Live Status
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Ignition</div>
                  <div className={`text-sm font-bold ${selectedFcrVehicleData.fcrData.ignition ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedFcrVehicleData.fcrData.ignition ? '🟢 ON' : '⚪ OFF'}
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">SOS</div>
                  <div className={`text-sm font-bold ${selectedFcrVehicleData.fcrData.sos ? 'text-red-600' : 'text-gray-400'}`}>
                    {selectedFcrVehicleData.fcrData.sos ? '🔴 ACTIVE' : '⚪ OFF'}
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg col-span-2 md:col-span-1">
                  <div className="text-xs text-gray-500">GPS Last Update</div>
                  <div className="text-sm font-mono text-gray-700">
                    {selectedFcrVehicleData.fcrData?.gps_time ? 
                      formatDateToLocal(selectedFcrVehicleData.fcrData.gps_time) : 
                      (selectedFcrVehicleData.fcrData?.gpsLastUpdate ? 
                        formatDateToLocal(selectedFcrVehicleData.fcrData.gpsLastUpdate) : 
                        'No GPS data')
                    }
                  </div>
                </div>
              </div>
              
              {selectedFcrVehicleData.fcrData.latitude && (
                <div className="mt-3 bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Location</div>
                  <div className="text-sm font-mono text-gray-700">
                    Lat: {Number(selectedFcrVehicleData.fcrData.latitude).toFixed(6)}, 
                    Long: {Number(selectedFcrVehicleData.fcrData.longitude).toFixed(6)}
                  </div>
                </div>
              )}
            </div>

            {allVehicleFcrData.length > 1 && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">All Vehicles in Query</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {allVehicleFcrData.map((vehicle, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${vehicle.vehicleName === selectedFcrVehicleData.vehicleName ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{vehicle.vehicleName}</div>
                          <div className="text-xs text-gray-500">FCR: {vehicle.reportNumber || vehicle.fcrData?.reportNumber || 'N/A'}</div>
                        </div>
                        {vehicle.vehicleName === selectedFcrVehicleData.vehicleName && (
                          <span className="text-xs bg-indigo-500 text-white px-2 py-0.5 rounded-full">Viewing</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <FaTruck className="text-6xl mx-auto mb-4 opacity-30" />
            <p className="text-lg">No FCR data available for this vehicle</p>
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-gray-200 flex justify-end flex-shrink-0 bg-gray-50 rounded-b-2xl">
        <button
          onClick={() => {
            setShowVehicleFcrModal(false);
            setSelectedFcrVehicleData(null);
            setAllVehicleFcrData([]);
          }}
          className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}

        {showLeaderboardModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[10002] p-4">
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[80vh] overflow-y-auto text-gray-800">
              <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FaTrophy className="text-emerald-500" /> Installer Leaderboard
                </h2>
                <button onClick={() => setShowLeaderboardModal(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600 w-16">Rank</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-600">Installer Name</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Total Installed</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Queries Attended</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Panic Buttons</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Activity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {leaderboardData.map((item, index) => {
                      let badge = `${index + 1}`;
                      if (index === 0) badge = "🥇 1st";
                      if (index === 1) badge = "🥈 2nd";
                      if (index === 2) badge = "🥉 3rd";

                      const isMe = String(item.username).toLowerCase() === String(currentUser?.username).toLowerCase();

                      return (
                        <tr key={item.id || index} className={`${isMe ? "bg-indigo-50 font-semibold" : index === 0 ? "bg-amber-50/40" : ""} hover:bg-gray-100/80`}>
                          <td className="px-4 py-2.5 text-center font-bold text-gray-700">{badge}</td>
                          <td className="px-4 py-2.5 text-gray-900">
                            {item.username} {isMe && <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded ml-1.5">You</span>}
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-indigo-600 text-base">
                            {item.installedCount}
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-emerald-600 text-base">
                            {item.queriesAttended}
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-orange-600 text-base">
                            {item.totalPanicButtons}
                          </td>
                          <td className="px-4 py-2.5 text-center text-gray-400 text-sm">
                            —
                          </td>
                        </tr>
                      );
                    })}
                    {leaderboardData.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-4 text-center text-gray-400">No data available to build leaderboard tracks.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ========== INSTALLER VIEW ==========
  if (isInstaller) {
    return (
      <>
        <GlobalNotificationListener />
        <div className="min-h-screen flex flex-col bg-gray-50">
          <header className="bg-gradient-to-r from-gray-900 to-gray-800 text-white p-3 sm:p-4 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                <img src="/nXExtendLogo.png" alt="Logo" className="h-8 w-auto dashboardlogo" />
                <span className="font-semibold text-xs sm:text-base md:text-lg whitespace-nowrap">
                  {isQueryUser ? "Log Portal" : "Vehicle Management"}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 md:gap-3">
                <span className="text-xs sm:text-sm opacity-90 whitespace-nowrap hidden sm:inline">
                  {currentUser?.username} {isQueryUser ? "(Terminal Account)" : "(Installer)"}
                </span>

                <button
                  onClick={() => setShowQueriesModal(true)}
                  className="flex items-center space-x-1 bg-amber-500 hover:bg-amber-600 text-white py-1 px-2 sm:py-1.5 sm:px-3 rounded-lg text-[10px] sm:text-sm transition font-medium shadow whitespace-nowrap"
                >
                  <FaClipboardList className="text-[10px] sm:text-sm" />
                  <span>My Queries</span>
                  <span>({installerQueries.length})</span>
                </button>

                <button
                  onClick={() => setShowLeaderboardModal(true)}
                  className="flex items-center space-x-1 bg-emerald-600 hover:bg-emerald-700 text-white py-1 px-2 sm:py-1.5 sm:px-3 rounded-lg text-[10px] sm:text-sm transition font-medium shadow whitespace-nowrap"
                >
                  <FaTrophy className="text-[10px] sm:text-sm" />
                  <span>Leaderboard</span>
                </button>

                <button 
                  onClick={() => {
                    if (currentUser?.role === 'installer') {
                      navigate('/login', {
                        state: {
                          triggerLogoutCamera: true
                        }
                      });
                    } else {
                      handleLogout();
                    }
                  }} 
                  className="flex items-center space-x-1 bg-red-500 hover:bg-red-600 text-white py-1 px-2 sm:py-1.5 sm:px-3 rounded-lg text-[10px] sm:text-sm transition whitespace-nowrap"
                >
                  <FaSignOutAlt className="text-[10px] sm:text-sm" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </header>
          <main className="flex-1 p-6">
            <Outlet context={{ users, currentUser, vehicles }} />
          </main>
        </div>

        {showQueriesModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-6xl mx-auto max-h-[85vh] overflow-y-auto text-gray-800">
              <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FaClipboardList className="text-amber-500" /> My Query Logs
                </h2>
                <button onClick={() => setShowQueriesModal(false)} className="text-gray-400 hover:text-gray-600 font-bold text-lg">✕</button>
              </div>

              <div className="flex space-x-2 mb-4 bg-gray-100 p-1 rounded-xl w-fit">
                <button
                  onClick={() => setQueryFilter('all')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${queryFilter === 'all' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  All Queries
                </button>
                <button
                  onClick={() => setQueryFilter('pending')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${queryFilter === 'pending' ? 'bg-amber-500 shadow text-white' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  <FaClock className="text-[10px]" /> Pending
                </button>
                <button
                  onClick={() => setQueryFilter('completed')}
                  className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${queryFilter === 'completed' ? 'bg-emerald-600 shadow text-white' : 'text-gray-500 hover:text-gray-900'}`}
                >
                  <FaCheckCircle className="text-[10px]" /> Completed
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Ticket ID</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Company</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Vehicle</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Category</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">Description</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600">FCR Number</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Query Created Time</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600">Status</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredQueries.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="px-4 py-8 text-center text-gray-400 font-medium">
                          No query records found for your account.
                        </td>
                      </tr>
                    ) : (
                      filteredQueries.map((query) => {
                        const currentStatus = String(query.status || '').toLowerCase();
                        const isDone = currentStatus === 'completed' || currentStatus === 'installed' || currentStatus === 'in use';
                        const isPendingApproval = currentStatus === 'pending approval';
                        const canComplete = !isDone && !isPendingApproval;
                        const fcrNumbers = getFcrNumbers(query);
                        const fcrDisplay = fcrNumbers.length > 0 ? fcrNumbers.join(', ') : '—';
                        const hasMultipleFcr = fcrNumbers.length > 1;

                        return (
                          <tr key={query.id || query.query_id || query.ticket_no} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-4 py-3 font-bold text-gray-900 whitespace-nowrap">{query.ticket_no || query.ticketNo || query.id}</td>
                            <td className="px-4 py-3 text-gray-700">{query.company_name || query.companyName}</td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => openVehiclePopup(query)}
                                className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-bold px-3 py-1.5 rounded-lg text-xs transition flex items-center gap-1"
                              >
                                <FaEye className="text-[10px]" /> View ({query.vehicle_count || query.vehicleCount || 0})
                              </button>
                            </td>
                            <td className="px-4 py-3 text-indigo-700 font-medium">{query.query_type || query.queryType}</td>
                            <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={query.description}>
                              {query.description}
                            </td>
                            <td className="px-4 py-3 font-mono text-xs font-semibold max-w-[150px]">
                              {fcrDisplay !== '—' ? (
                                <div className="relative group inline-block">
                                  <span className="text-emerald-600 bg-emerald-50 px-2 py-1 rounded text-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] block">
                                    {fcrDisplay}
                                  </span>
                                  {hasMultipleFcr && (
                                    <div className="absolute bottom-full left-0 mb-1 hidden group-hover:block bg-gray-900 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap z-10 shadow-lg">
                                      <div className="font-mono">
                                        {fcrNumbers.map((fcr, idx) => (
                                          <div key={idx} className="py-0.5">{fcr}</div>
                                        ))}
                                      </div>
                                      <div className="absolute -bottom-1 left-3 w-2 h-2 bg-gray-900 rotate-45"></div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-xs font-mono text-gray-500 whitespace-nowrap">
                              {formatDateToLocal(query.created_at || query.createdAt)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${isDone
                                ? 'bg-emerald-100 text-emerald-800'
                                : isPendingApproval
                                  ? 'bg-purple-100 text-purple-800'
                                  : currentStatus === 'ongoing'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-amber-100 text-amber-800'
                                }`}>
                                {isPendingApproval ? 'Pending Approval' : (query.status || 'Pending')}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {canComplete && (
                                <button
                                  onClick={() => handleCompleteQuery(query.id || query.query_id)}
                                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition shadow-sm"
                                >
                                  Mark Complete
                                </button>
                              )}
                              {isPendingApproval && (
                                <span className="text-xs text-purple-600 font-medium bg-purple-50 px-3 py-1 rounded-full">
                                  Awaiting Approval
                                </span>
                              )}
                              {isDone && (
                                <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-3 py-1 rounded-full">
                                  ✅ Done
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {showVehiclePopup && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-6 relative border border-gray-100 my-8">
              <button
                onClick={() => setShowVehiclePopup(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
              >
                <FaTimes size={20} />
              </button>
              <h2 className="text-lg font-bold text-gray-900 mb-4">Vehicle Details</h2>
              <p className="text-sm text-gray-500 mb-4">Total Vehicles: {popupCurrentVehicleList.length}</p>

              <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex-1 min-w-[150px]">
                  <label className="block text-xs font-bold text-gray-600 mb-1">Filter by Status</label>
                  <select
                    value={popupStatusFilter}
                    onChange={(e) => setPopupStatusFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                  >
                    <option value="">All Status</option>
                    <option value="Pending">Pending</option>
                    <option value="Ongoing">Ongoing</option>
                    <option value="Completed">Completed (Request Approval)</option>
                    <option value="Incomplete">Incomplete</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  onClick={handleStatusUpdateForSelected}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-4 py-2 rounded-lg transition flex items-center gap-1"
                >
                  <FaUserCheck /> Update Status
                </button>
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-xs font-bold text-gray-600">Select</th>
                      <th className="p-2 text-xs font-bold text-gray-600">#</th>
                      <th className="p-2 text-xs font-bold text-gray-600">Vehicle Number</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {popupCurrentVehicleList.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="p-4 text-center text-gray-400 text-sm">
                          No vehicles found for this query
                        </td>
                      </tr>
                    ) : (
                      popupCurrentVehicleList.map((vehicle, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedVehicles.includes(vehicle)}
                              onChange={() => toggleVehicleSelection(vehicle)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="p-2 text-xs font-medium text-gray-600">{index + 1}</td>
                          <td className="p-2 text-sm font-medium text-gray-700">{vehicle}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  {selectedVehicles.length} of {popupCurrentVehicleList.length} vehicles selected
                </span>
                <button
                  onClick={() => setShowVehiclePopup(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {showLeaderboardModal && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[80vh] overflow-y-auto text-gray-800">
              <div className="flex justify-between items-center mb-4 border-b border-gray-100 pb-3">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FaTrophy className="text-emerald-500" /> Installer Leaderboard
                </h2>
                <button onClick={() => setShowLeaderboardModal(false)} className="text-gray-400 hover:text-gray-600 font-bold">✕</button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600 w-16">Rank</th>
                      <th className="px-4 py-2 text-left font-semibold text-gray-600">Installer Name</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Total Installed</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Queries Attended</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Panic Buttons</th>
                      <th className="px-4 py-2 text-center font-semibold text-gray-600">Activity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {leaderboardData.map((item, index) => {
                      let badge = `${index + 1}`;
                      if (index === 0) badge = "🥇 1st";
                      if (index === 1) badge = "🥈 2nd";
                      if (index === 2) badge = "🥉 3rd";

                      const isMe = String(item.username).toLowerCase() === String(currentUser?.username).toLowerCase();

                      return (
                        <tr key={item.id || index} className={`${isMe ? "bg-indigo-50 font-semibold" : index === 0 ? "bg-amber-50/40" : ""} hover:bg-gray-100/80`}>
                          <td className="px-4 py-2.5 text-center font-bold text-gray-700">{badge}</td>
                          <td className="px-4 py-2.5 text-gray-900">
                            {item.username} {isMe && <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded ml-1.5">You</span>}
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-indigo-600 text-base">
                            {item.installedCount}
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-emerald-600 text-base">
                            {item.queriesAttended}
                          </td>
                          <td className="px-4 py-2.5 text-center font-bold text-orange-600 text-base">
                            {item.totalPanicButtons}
                          </td>
                          <td className="px-4 py-2.5 text-center text-gray-400 text-sm">
                            —
                          </td>
                        </tr>
                      );
                    })}
                    {leaderboardData.length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-4 text-center text-gray-400">No data available to build leaderboard tracks.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ============================================================
  // ADMIN / SUPER ADMIN VIEW
  // ============================================================
  return (
    <>
      <GlobalNotificationListener />
      <div className="min-h-screen flex bg-gray-50" style={{ minHeight: '100vh' }}>
        {isMobile && (
          <button
            onClick={toggleSidebar}
            className="fixed top-4 left-4 z-[9999] p-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg transition-all duration-200"
            style={{
              position: 'fixed',
              top: '16px',
              left: '16px',
              zIndex: 99999,
              minWidth: '48px',
              minHeight: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            aria-label="Toggle navigation menu"
          >
            {isSidebarOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
          </button>
        )}

        {isMobile && isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={closeSidebar}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          />
        )}

        <aside
          className="fixed top-0 left-0 z-[9998] w-72 lg:w-64 h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white flex flex-col shadow-2xl overflow-y-auto"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            height: '100vh',
            overflowY: 'auto',
            zIndex: 9998,
            transform: isMobile ? (isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)',
            transition: 'transform 0.3s ease-in-out'
          }}
        >
          <div className="p-6 border-b border-gray-700 flex justify-center flex-shrink-0">
            <img src="/nXExtendLogo.png" alt="Company Logo" className="h-10 w-auto object-contain drop-shadow" />
          </div>

          {isSuperAdmin && (
            <div className="px-3 py-3 border-b border-gray-700">
              <button
                onClick={() => {
                  setShowNotificationsModal(true);
                  refreshNotifications();
                }}
                className="relative w-full flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 py-2.5 px-3 rounded-lg transition"
              >
                <FaBell className="text-lg" />
                <span className="text-sm font-medium">Notifications</span>
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                    {notificationCount}
                  </span>
                )}
              </button>
            </div>
          )}

          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {(isSuperAdmin || isAdmin) && (
              <SidebarLink to="/dashboard" icon={<FaTachometerAlt />} label="Dashboard" onClick={closeSidebar} />
            )}

            {isSuperAdmin && (
              <div>
                <button
                  onClick={() => setCompanyOpen(!companyOpen)}
                  className="flex items-center justify-between w-full py-2.5 px-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <FaBuilding className="text-lg" />
                    <span>Register</span>
                  </div>
                  {companyOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {companyOpen && (
                  <div className="ml-6 space-y-1 mt-1">
                    <SidebarLink to="/company-details" icon={<FaBuilding />} label="Company" onClick={closeSidebar} />
                    <SidebarLink to="/sub-companies" icon={<FaSitemap />} label="Sub Companies" onClick={closeSidebar} />
                  </div>
                )}
              </div>
            )}

            {isSuperAdmin && (
              <div>
                <button
                  onClick={() => setRegisterOpen(!registerOpen)}
                  className="flex items-center justify-between w-full py-2.5 px-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <FaUserShield className="text-lg" />
                    <span>Sign Up</span>
                  </div>
                  {registerOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {registerOpen && (
                  <div className="ml-6 space-y-1 mt-1">
                    <SidebarLink to="/admin-signup" icon={<FaUserShield />} label="Admin" onClick={closeSidebar} />
                    <SidebarLink to="/installer-signup" icon={<FaUserCog />} label="Installer" onClick={closeSidebar} />
                    <SidebarLink to="/team-lead-signup" icon={<FaUserTie />} label="TeamLead" onClick={closeSidebar} />
                  </div>
                )}
              </div>
            )}

            <SidebarLink to="/device-inventory" icon={<FaMicrochip />} label="Device Inventory" onClick={closeSidebar} />
            <SidebarLink to="/sim-inventory" icon={<FaSimCard />} label="Sim Inventory" onClick={closeSidebar} />
            <SidebarLink to="/vehicle-details" icon={<FaCar />} label="Vehicle Details" onClick={closeSidebar} />

            {(isSuperAdmin || ((isAdmin && showLogPage)) || isTeamLead ) && (
              <SidebarLink to="/log-page" icon={<FaClipboardList />} label="Complaints" onClick={closeSidebar} />
            )}

            {isSuperAdmin && (
              <div>
                <button
                  onClick={() => setReportsOpen(!reportsOpen)}
                  className="flex items-center justify-between w-full py-2.5 px-3 rounded-lg text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <FaChartBar className="text-lg" />
                    <span>Reports</span>
                  </div>
                  {reportsOpen ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                {reportsOpen && (
                  <div className="ml-6 space-y-1 mt-1">
                    <SidebarLink to="/reports" icon={<FaClipboardList />} label="Query Reports" onClick={closeSidebar} />
                    <SidebarLink to="/device-reports" icon={<FaMicrochip />} label="Device Reports" onClick={closeSidebar} />
                    <SidebarLink to="/installer-reports" icon={<FaUsers />} label="Installer Reports" onClick={closeSidebar} />
                  </div>
                )}
              </div>
            )}
          </nav>

          <div className="p-4 border-t border-gray-700 flex-shrink-0">
            <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Logged in as</div>
            <div className="text-sm font-medium">{currentUser?.username} <span className="text-gray-400">({currentUser?.role})</span></div>
            <button onClick={() => { handleLogout(); closeSidebar(); }} className="mt-3 w-full flex items-center justify-center space-x-2 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg text-sm transition">
              <FaSignOutAlt />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <main
          className="flex-1 min-h-screen bg-gray-50"
          style={{
            marginLeft: isMobile ? '0' : '16rem',
            width: isMobile ? '100%' : 'auto',
            minHeight: '100vh',
            flex: 1,
            position: 'relative'
          }}
        >
          {isMobile && <div className="h-16"></div>}
          <div className="p-3 sm:p-4 md:p-6 lg:p-8 w-full max-w-full overflow-x-hidden">
            <Outlet context={{ queriesList, teamLeadInstallerNames, vehicles }} />
          </div>
        </main>
      </div>

      {showNotificationsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-auto max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 rounded-t-2xl flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <FaBell className="text-white text-xl" />
                <h2 className="text-white text-xl font-bold">
                  Pending Approvals ({notifications.length})
                </h2>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowNotificationsModal(false);
                    navigate('/log-page');
                  }}
                  className="text-white/80 hover:text-white text-sm bg-white/10 px-3 py-1.5 rounded-lg flex items-center gap-2 transition"
                >
                  <FaClipboardList className="text-xs" /> View All
                </button>
                <button
                  onClick={() => setShowNotificationsModal(false)}
                  className="text-white/80 hover:text-white text-2xl transition"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <FaBell className="text-6xl mb-4 opacity-30" />
                  <p className="text-lg font-medium">No pending approvals</p>
                  <p className="text-sm">All caught up!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map((n) => {
                    const fcrReport = n.fcrData || null;
                    const reportNumber = fcrReport?.reportNumber || n.serial_number || 'N/A';
                    const allVehicles = n.allVehicleFcrData || [];
                    const hasFcrData = n.hasFcrData || false;
                    const showLiveStatus = n.showLiveStatus || false;
                    const gpsLastUpdate = n.gpsLastUpdate || null;
                    
                    const formatGpsTime = (gpsTime) => {
                      if (!gpsTime) return 'No GPS data';
                      try {
                        const date = new Date(gpsTime);
                        if (isNaN(date.getTime())) return 'No GPS data';
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${day}-${month}-${year} ${hours}:${minutes}`;
                      } catch (e) {
                        return 'No GPS data';
                      }
                    };
                    
                    return (
                      <div
                        key={n.id}
                        className="border rounded-xl p-5 transition shadow-sm bg-blue-50 border-blue-200 hover:shadow-md"
                      >
                        <div className="flex justify-between items-start mb-2 flex-wrap gap-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-bold px-3 py-1.5 rounded-full bg-blue-500 text-white flex items-center gap-1.5">
                              <FaClock className="text-[11px]" /> ⏳ PENDING APPROVAL
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDateToLocal(n.created_at || n.createdAt)}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              onClick={() => handleDirectApproval(n)}
                              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition shadow-md"
                            >
                              <FaCheck className="text-sm" />
                              Approve
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
                          <div className="flex items-center gap-1">
                            <FaTicketAlt className="text-blue-500 text-[10px]" />
                            <span className="text-gray-600">Ticket:</span>
                            <span className="text-gray-900 font-mono font-bold">{n.ticket_no || `#${n.id}`}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <FaBuilding className="text-purple-500 text-[10px]" />
                            <span className="text-gray-600">Company:</span>
                            <span className="text-gray-900 truncate">{n.company_name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <FaUserCog className="text-indigo-500 text-[10px]" />
                            <span className="text-gray-600">Installer:</span>
                            <span className="text-gray-900">{n.installer_name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-1 col-span-2 md:col-span-1">
                            <FaTruck className="text-emerald-500 text-[10px]" />
                            <span className="text-gray-600">Vehicles:</span>
                            <span className="text-gray-900 text-xs truncate" title={n.vehicle_name}>
                              {n.vehicle_name || 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <FaMicrochip className="text-emerald-600 text-[10px]" />
                            <span className="text-gray-600">FCR:</span>
                            {allVehicles.length > 0 ? (
                              <span className="text-emerald-700 font-mono font-bold text-[10px]">
                                {allVehicles.length} found
                              </span>
                            ) : (
                              <span className="text-gray-400 text-[10px] italic">No FCR</span>
                            )}
                          </div>
                          
                        </div>

                        {allVehicles.length > 0 && (
                          <div className="mt-2">
                            <div className="text-xs font-semibold text-gray-600 mb-1.5">Vehicle FCR Details:</div>
                            <div className="flex flex-wrap gap-1.5">
                              {allVehicles.map((item, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => handleOpenVehicleFcrModal(item, allVehicles)}
                                  className="text-xs bg-white px-3 py-1 rounded border border-gray-200 flex items-center gap-1.5 hover:bg-gray-50 hover:border-indigo-300 transition"
                                >
                                  <span className="font-medium text-gray-700">{item.vehicleName}</span>
                                  {item.fcrData?.reportNumber && (
                                    <span className="text-emerald-600 font-mono text-[10px]">
                                      FCR: {item.fcrData.reportNumber}
                                    </span>
                                  )}
                                  <FaEye className="text-indigo-400 text-[10px] ml-1" />
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500">
                          Installer <span className="font-medium text-gray-700">{n.installer_name || 'Unknown'}</span> submitted vehicle records awaiting approval.
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 flex justify-between items-center flex-shrink-0 bg-gray-50 rounded-b-2xl flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-gray-500">
                  Total: <span className="font-bold">{notifications.length}</span> pending
                </span>
                <button
                  onClick={() => {
                    setNotifications([]);
                    setNotificationCount(0);
                    setShowNotificationsModal(false);
                  }}
                  className="px-3 py-1.5 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-xs font-medium transition flex items-center gap-1"
                >
                  <FaSync className="text-xs" /> Fetch All
                </button>
              </div>
              <button
                onClick={() => setShowNotificationsModal(false)}
                className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showVehicleFcrModal && selectedFcrVehicleData && (
  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-auto max-h-[90vh] flex flex-col">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-4 rounded-t-2xl flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-3">
          <FaTruck className="text-white text-xl" />
          <h2 className="text-white text-xl font-bold">
            Vehicle Live Data - {selectedFcrVehicleData.vehicleName}
          </h2>
        </div>
        <button
          onClick={() => {
            setShowVehicleFcrModal(false);
            setSelectedFcrVehicleData(null);
            setAllVehicleFcrData([]);
          }}
          className="text-white/80 hover:text-white text-2xl transition"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {selectedFcrVehicleData.fcrData ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">Vehicle Number</div>
                <div className="text-base font-bold text-gray-900">{selectedFcrVehicleData.vehicleName || 'N/A'}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">FCR Report Number</div>
                <div className="text-base font-bold text-emerald-600">
                  {selectedFcrVehicleData.reportNumber || selectedFcrVehicleData.fcrData?.reportNumber || 'N/A'}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">IMEI</div>
                <div className="text-sm font-mono text-gray-700">{selectedFcrVehicleData.imei || selectedFcrVehicleData.fcrData?.imei || 'N/A'}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">SIM Number</div>
                <div className="text-sm font-mono text-gray-700">{selectedFcrVehicleData.simNo || selectedFcrVehicleData.fcrData?.simNo || 'N/A'}</div>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <FaEye className="text-indigo-500" /> Live Status
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Ignition</div>
                  <div className={`text-sm font-bold ${selectedFcrVehicleData.fcrData.ignition ? 'text-green-600' : 'text-gray-400'}`}>
                    {selectedFcrVehicleData.fcrData.ignition ? '🟢 ON' : '⚪ OFF'}
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">SOS</div>
                  <div className={`text-sm font-bold ${selectedFcrVehicleData.fcrData.sos ? 'text-red-600' : 'text-gray-400'}`}>
                    {selectedFcrVehicleData.fcrData.sos ? '🔴 ACTIVE' : '⚪ OFF'}
                  </div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg col-span-2 md:col-span-1">
                  <div className="text-xs text-gray-500">GPS Last Update</div>
                  <div className="text-sm font-mono text-gray-700">
                    {selectedFcrVehicleData.fcrData?.gps_time ? 
                      formatDateToLocal(selectedFcrVehicleData.fcrData.gps_time) : 
                      (selectedFcrVehicleData.fcrData?.gpsLastUpdate ? 
                        formatDateToLocal(selectedFcrVehicleData.fcrData.gpsLastUpdate) : 
                        'No GPS data')
                    }
                  </div>
                </div>
              </div>
              
              {selectedFcrVehicleData.fcrData.latitude && (
                <div className="mt-3 bg-gray-50 p-3 rounded-lg">
                  <div className="text-xs text-gray-500">Location</div>
                  <div className="text-sm font-mono text-gray-700">
                    Lat: {Number(selectedFcrVehicleData.fcrData.latitude).toFixed(6)}, 
                    Long: {Number(selectedFcrVehicleData.fcrData.longitude).toFixed(6)}
                  </div>
                </div>
              )}
            </div>

            {allVehicleFcrData.length > 1 && (
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">All Vehicles in Query</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {allVehicleFcrData.map((vehicle, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${vehicle.vehicleName === selectedFcrVehicleData.vehicleName ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-gray-50'}`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{vehicle.vehicleName}</div>
                          <div className="text-xs text-gray-500">FCR: {vehicle.reportNumber || vehicle.fcrData?.reportNumber || 'N/A'}</div>
                        </div>
                        {vehicle.vehicleName === selectedFcrVehicleData.vehicleName && (
                          <span className="text-xs bg-indigo-500 text-white px-2 py-0.5 rounded-full">Viewing</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <FaTruck className="text-6xl mx-auto mb-4 opacity-30" />
            <p className="text-lg">No FCR data available for this vehicle</p>
          </div>
        )}
      </div>

      <div className="px-6 py-3 border-t border-gray-200 flex justify-end flex-shrink-0 bg-gray-50 rounded-b-2xl">
        <button
          onClick={() => {
            setShowVehicleFcrModal(false);
            setSelectedFcrVehicleData(null);
            setAllVehicleFcrData([]);
          }}
          className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition"
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
    </>
  );
};

const SidebarLink = ({ to, icon, label, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center space-x-3 py-2.5 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${isActive
        ? 'bg-indigo-600 text-white shadow-lg'
        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`}
    >
      <span className="text-lg">{icon}</span>
      <span>{label}</span>
    </Link>
  );
};

export default Layout;